import React, { useState } from "react";
import axios from "axios";
import { baseURL } from "./config";
import { useNavigate } from "react-router-dom";
import { clearUserCache } from "./utils/userCache";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiUserPlus, FiEye, FiEyeOff } from "react-icons/fi";
import { FaUser, FaPlus } from "react-icons/fa";
import PageHeader from "./components/reusable/PageHeader";

const AddUserPage = () => {
  const [formData, setFormData] = useState({
    mobileNumber: "",
    name: "",
    password: "",
    confirmPassword: "",
    image: null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    const mobileRegex = /^[6-9]\d{9}$/;

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!mobileRegex.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
    } else if (/^(\d)\1{9}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Invalid mobile number";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Full name is required";
    } else if (formData.name.length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    } else if (/[^a-zA-Z\s]/.test(formData.name)) {
      newErrors.name = "Name should only contain letters and spaces";
    }

    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&]).{6,}$/;
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password =
        "Password must include a letter, number, and special character";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (formData.image) {
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!allowedTypes.includes(formData.image.type)) {
        newErrors.image = "Only JPG or PNG images allowed";
      }
      if (formData.image.size > 2 * 1024 * 1024) {
        newErrors.image = "Image size must be under 2MB";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (errors.image) setErrors({ ...errors, image: "" });

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setErrors({ ...errors, image: "Only JPG or PNG images allowed" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrors({ ...errors, image: "Image size must be under 2MB" });
      return;
    }

    setFormData((prev) => ({ ...prev, image: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("mobileNumber", formData.mobileNumber);
      payload.append("name", formData.name);
      payload.append("password", formData.password);
      if (formData.image) payload.append("image", formData.image);

      await axios.post(`${baseURL}/addUser`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearUserCache();

      toast.success("User added successfully!", {
        autoClose: 1500,
        onClose: () => navigate("/customers"),
      });
    } catch (error) {
      console.error("Error adding user:", error);
      let errorMessage = "An error occurred. Please try again.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full flex flex-col  justify-center bg-gray-100   overflow-y-scroll">
      <PageHeader title="Add New Customer"/>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-8">


        <form onSubmit={handleSubmit}>
          {/* Image Upload */}
          <div className="flex justify-center mb-5">
            <div className="relative">
              {formData.image ? (
                <img
                  src={URL.createObjectURL(formData.image)}
                  alt="profile preview"
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow"
                />
              ) : (
                <div className="bg-gray-200 border-2 border-dashed rounded-full w-24 h-24 flex items-center justify-center">
                  <FaUser className="text-gray-400 text-2xl" />
                </div>
              )}
              <label className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-md cursor-pointer border border-gray-200">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <FaPlus className="text-blue-600 text-xs" />
              </label>
            </div>
          </div>
          {errors.image && (
            <p className="text-red-500 text-sm mb-3 text-center">{errors.image}</p>
          )}

          {/* Mobile */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number
            </label>
            <input
              type="text"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.mobileNumber ? "border-red-500" : ""
              }`}
              placeholder="e.g. 9876543210"
            />
            {errors.mobileNumber && (
              <p className="text-red-500 text-sm">{errors.mobileNumber}</p>
            )}
          </div>

          {/* Name */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.name ? "border-red-500" : ""
              }`}
              placeholder="Enter full name"
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>

          {/* Password */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg pr-10 ${
                  errors.password ? "border-red-500" : ""
                }`}
                placeholder="Create a password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg pr-10 ${
                  errors.confirmPassword ? "border-red-500" : ""
                }`}
                placeholder="Confirm your password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white shadow-md transition ${
              isSubmitting
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSubmitting ? "Creating User..." : "Create User"}
          </button>
        </form>
      </div>
      <ToastContainer position="top-right" theme="colored" />
    </div>
  );
};

export default AddUserPage;
